/** Retains a completed DOM composition and canvas pixels without image encoding. */
export class RetainedComposition {
  private picture: HTMLElement | undefined;
  constructor(private readonly document: Document) {}

  async capture(valid: () => boolean = () => true): Promise<boolean> {
    const root = this.document.querySelector<HTMLElement>('#root');
    if (!root) return valid();
    const copy = root.cloneNode(true) as HTMLElement;
    const copies = copy.querySelectorAll('canvas');
    // Bitmap transfer avoids synchronous WebGL-to-2D readback on supporting engines.
    // Snapshot the DOM before awaiting pixels so both represent the same completed frame.
    await Promise.all([...root.querySelectorAll('canvas')].map(async (canvas, index) => {
      const target = copies[index]!;
      if (typeof createImageBitmap === 'function') {
        const renderer = target.getContext('bitmaprenderer');
        if (renderer) {
          const bitmap = await createImageBitmap(canvas);
          try { renderer.transferFromImageBitmap(bitmap); } finally { bitmap.close(); }
          return;
        }
      }
      target.getContext('2d')?.drawImage(canvas, 0, 0);
    }));
    if (!valid()) { copies.forEach(canvas => { canvas.width = 0; }); return false; }
    copy.querySelectorAll('script').forEach(script => script.remove());
    const picture = this.document.createElement('div');
    picture.dataset.retainedComposition = 'true';
    picture.inert = true; picture.setAttribute('aria-hidden', 'true');
    picture.style.cssText = 'position:fixed;inset:0;z-index:2147483647;pointer-events:none;background:#000;overflow:hidden';
    picture.appendChild(copy);
    this.dispose(); this.picture = picture;
    return true;
  }

  hold(): void { if (this.picture && !this.picture.isConnected) this.document.body.appendChild(this.picture); }
  release(): void { this.picture?.remove(); }
  dispose(): void {
    this.release(); this.picture?.querySelectorAll('canvas').forEach(canvas => { canvas.width = 0; });
    this.picture = undefined;
  }
}
